import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import { CONFIRM_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { matchesUrlName } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import { isTeamOwnerActor } from '../../../data/teamConfig';
import {
  ALL_SCOPE_SELECTION_LABEL,
  APPLICATION_AREA_OPTIONS,
  CONFIGURATION_STATUS,
  KEYWORD_CONFIG_ROUTE,
  KEYWORD_USE_OPTIONS,
  RULE_SCOPE,
  RULE_SCOPE_OPTIONS,
  buildSampleKeywordConfigRules,
  createDefaultKeywordRuleForm,
  formatScopeSelectionDisplay,
  getKeywordConfigSources,
  getSourceSubcategoryOptions,
  getSourceTradeCategoryOptions,
  normalizeKeywordRule,
} from '../../../data/keywordsConfig';
import {
  buildKeywordConfigActor,
  collectKeywordRuleErrors,
  mergeRulesAfterCreate,
  mergeRulesAfterUpdate,
} from '../../../data/keywordConfigWorkflow';

const ConfirmModal = ({ title, message, confirmLabel, onConfirm, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${CONFIRM_POPUP_CONTENT_WIDTH_CLASS}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="px-5 pt-5 pb-3 border-b border-lightgray">
        <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
      </div>
      <div className="px-5 py-4">
        <AdminSupportingText className="text-offblack text-center">{message}</AdminSupportingText>
      </div>
      <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
        <AdminFormSecondaryAction type="button" onClick={onClose}>
          Cancel
        </AdminFormSecondaryAction>
        <AdminHeaderAction type="button" onClick={onConfirm}>
          {confirmLabel}
        </AdminHeaderAction>
      </div>
    </div>
  </div>
);

const RequiredMark = () => <span className="text-alertred">*</span>;

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const ReadOnlyField = ({ label, value, mono = false, textOnly = false }) => {
  if (textOnly) {
    return <TextValue label={label} value={value} mono={mono} />;
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <input
        type="text"
        value={value || '—'}
        readOnly
        className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
      />
    </div>
  );
};

const TextInput = ({
  label,
  value,
  onChange,
  readOnly,
  required,
  error,
  placeholder,
  mono = false,
}) => {
  if (readOnly) {
    return <TextValue label={label} value={value} mono={mono} />;
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <RequiredMark /> : null}
      </AdminDetailFieldLabel>
      <input
        type="text"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${adminFormDetailInputClasses(false, Boolean(error))}${mono ? ' font-mono' : ''}`}
      />
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const StackedSelect = ({
  label,
  value,
  options,
  onChange,
  readOnly,
  required,
  error,
  placeholder,
  disabled,
}) => {
  const selected = options.find((o) => o.value === value);
  if (readOnly) {
    return <TextValue label={label} value={selected?.label || value || '—'} />;
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <RequiredMark /> : null}
      </AdminDetailFieldLabel>
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={adminFormDetailSelectClasses(Boolean(disabled), Boolean(error))}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <AdminFormDropdownChevron />
      </div>
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const MultiSelectChecklist = ({ label, options, selectedIds, onChange, readOnly, required, error }) => {
  const selected = new Set(selectedIds || []);
  if (readOnly) {
    const labels = options.filter((o) => selected.has(o.id)).map((o) => o.label);
    return <TextValue label={label} value={labels.length ? labels.join('; ') : '—'} />;
  }
  return (
    <div className="space-y-2 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <RequiredMark /> : null}
      </AdminDetailFieldLabel>
      <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3 max-h-56 overflow-y-auto">
        {options.length === 0 ? (
          <AdminSupportingText className="text-mediumgray">No options available.</AdminSupportingText>
        ) : (
          options.map((opt) => (
            <label key={opt.id} className="flex items-start gap-2 text-sm text-offblack">
              <input
                type="checkbox"
                className="accent-primaryblue mt-0.5 shrink-0"
                checked={selected.has(opt.id)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(opt.id);
                  else next.delete(opt.id);
                  onChange([...next]);
                }}
              />
              <span className="break-words">{opt.label}</span>
            </label>
          ))
        )}
      </div>
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const KeywordRuleDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { keywordName: ruleNameParam } = useParams();
  const { auth } = useAuth();
  const canAccess = isTeamOwnerActor(auth);
  const actor = useMemo(() => buildKeywordConfigActor(auth), [auth]);
  const sources = useMemo(() => getKeywordConfigSources(), []);
  const tradeCategoryOptions = useMemo(() => getSourceTradeCategoryOptions(sources), [sources]);

  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isAdd && !isEdit;

  const existingRules = useMemo(() => {
    if (Array.isArray(location.state?.existingRules)) return location.state.existingRules;
    return buildSampleKeywordConfigRules(sources);
  }, [location.state?.existingRules, sources]);

  const nameParam = decodeURIComponent(ruleNameParam || '');

  const [formData, setFormData] = useState(() => {
    if (isAdd) return createDefaultKeywordRuleForm();
    const fromState = location.state?.rule;
    const found =
      (fromState && matchesUrlName(fromState.ruleName, nameParam) ? fromState : null) ||
      existingRules.find((r) => matchesUrlName(r.ruleName, nameParam));
    return found ? normalizeKeywordRule(found, sources) : createDefaultKeywordRuleForm();
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (isAdd) {
      setFormData(createDefaultKeywordRuleForm());
      return;
    }
    const fromState = location.state?.rule;
    const found =
      (fromState && matchesUrlName(fromState.ruleName, nameParam) ? fromState : null) ||
      existingRules.find((r) => matchesUrlName(r.ruleName, nameParam));
    if (found) setFormData(normalizeKeywordRule(found, sources));
  }, [isAdd, nameParam, location.state?.rule, existingRules, sources]);

  const subcategoryOptions = useMemo(
    () => getSourceSubcategoryOptions(formData.parentTradeCategoryIds || [], sources),
    [formData.parentTradeCategoryIds, sources],
  );

  const scopeSelectionDisplay = useMemo(
    () => formatScopeSelectionDisplay(formData, sources),
    [formData, sources],
  );

  const parentSelectionDisplay = useMemo(() => {
    if (!(formData.parentTradeCategoryIds || []).length) return '—';
    return tradeCategoryOptions
      .filter((o) => (formData.parentTradeCategoryIds || []).includes(o.id))
      .map((o) => o.label)
      .join('; ');
  }, [formData.parentTradeCategoryIds, tradeCategoryOptions]);

  const pageHeading = isAdd
    ? 'Add Keyword Rule'
    : isEdit
      ? 'Edit Keyword Rule'
      : 'Keyword Rule Detail';

  const statusDisplay = isAdd
    ? 'Active after Save & Confirm'
    : formData.configurationStatus || '—';

  const goBackToList = (nextRules = existingRules, feedback) => {
    navigate(KEYWORD_CONFIG_ROUTE, {
      state: {
        rules: nextRules,
        ...(feedback ? { feedback } : {}),
      },
    });
  };

  const updateField = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'ruleScope') {
        next.tradeCategoryIds = [];
        next.parentTradeCategoryIds = [];
        next.subcategoryIds = [];
      }
      if (key === 'parentTradeCategoryIds') {
        const allowed = new Set(
          getSourceSubcategoryOptions(value, sources).map((o) => o.id),
        );
        next.subcategoryIds = (prev.subcategoryIds || []).filter((id) => allowed.has(id));
      }
      return next;
    });
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next._overlap;
      return next;
    });
  };

  const handleSaveClick = () => {
    const payload = {
      ...formData,
      configurationStatus: isAdd
        ? CONFIGURATION_STATUS.ACTIVE
        : formData.configurationStatus || CONFIGURATION_STATUS.ACTIVE,
    };
    const errors = collectKeywordRuleErrors(payload, existingRules, sources, {
      excludeId: isAdd ? undefined : formData.id,
      checkOverlap: isAdd || payload.configurationStatus === CONFIGURATION_STATUS.ACTIVE,
    });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setConfirm({
      title: isAdd ? 'Save & Confirm Rule?' : 'Save & Confirm Changes?',
      message: isAdd
        ? 'Save and activate this Keyword configuration rule?'
        : 'Save and confirm these configuration changes?',
      confirmLabel: 'Confirm',
      payload,
    });
  };

  const handleConfirmSave = () => {
    const payload = confirm?.payload || formData;
    const result = isAdd
      ? mergeRulesAfterCreate(existingRules, payload, actor, sources)
      : mergeRulesAfterUpdate(existingRules, payload, actor, sources);

    setConfirm(null);
    if (!result.success) {
      setFieldErrors(result.errors || {});
      return;
    }
    goBackToList(result.rules, result.message);
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Keyword Rule</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          Configuration is developer-controlled. You do not have permission to view Keyword Rules.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (
    !isAdd &&
    !existingRules.find((r) => matchesUrlName(r.ruleName, nameParam)) &&
    !location.state?.rule
  ) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Keyword Rule not found</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          Keyword Rule not found.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <div className="min-w-0">
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageHeading}</AdminPageTitle>

          <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className={adminFormTwoColGridClass}>
                <TextValue label="Rule ID" value={formData.id || '—'} mono />
                <TextValue label="Rule Name" value={formData.ruleName || '—'} />
                <TextValue label="Application Area" value={formData.applicationArea || '—'} />
                <TextValue label="Rule Scope" value={formData.ruleScope || '—'} />
                {formData.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES ? (
                  <TextValue label="Parent Trade Category Selection" value={parentSelectionDisplay} />
                ) : null}
                <TextValue label="Scope Selection" value={scopeSelectionDisplay || '—'} />
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0">
                  <TextValue label="Keyword Use" value={formData.keywordUse || '—'} />
                  <TextValue label="Configuration Status" value={statusDisplay} />
                </div>
              </div>
            </DashboardMainContainer>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageHeading}
        onSubmit={handleSaveClick}
        beforeForm={
          fieldErrors._overlap ? (
            <AdminFormFieldError className="mb-4">{fieldErrors._overlap}</AdminFormFieldError>
          ) : null
        }
        afterForm={
          confirm ? (
            <ConfirmModal
              title={confirm.title}
              message={confirm.message}
              confirmLabel={confirm.confirmLabel}
              onConfirm={handleConfirmSave}
              onClose={() => setConfirm(null)}
            />
          ) : null
        }
      >
        {!isAdd ? (
          <ReadOnlyField
            label="Rule ID"
            value={formData.id || '—'}
            mono
          />
        ) : null}

        <TextInput
          label="Rule Name"
          value={formData.ruleName || ''}
          onChange={(v) => updateField('ruleName', v)}
          required
          error={fieldErrors.ruleName}
          placeholder="Enter Rule Name"
        />

        <StackedSelect
          label="Application Area"
          value={formData.applicationArea}
          options={APPLICATION_AREA_OPTIONS}
          onChange={(v) => updateField('applicationArea', v)}
          required
          error={fieldErrors.applicationArea}
          placeholder="Select Application Area"
        />

        <StackedSelect
          label="Rule Scope"
          value={formData.ruleScope}
          options={RULE_SCOPE_OPTIONS}
          onChange={(v) => updateField('ruleScope', v)}
          required
          error={fieldErrors.ruleScope}
          placeholder="Select Rule Scope"
        />

        {formData.ruleScope === RULE_SCOPE.ALL_KEYWORDS ? (
          <ReadOnlyField label="Scope Selection" value={ALL_SCOPE_SELECTION_LABEL} />
        ) : null}

        {formData.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES ? (
          <MultiSelectChecklist
            label="Scope Selection"
            options={tradeCategoryOptions}
            selectedIds={formData.tradeCategoryIds}
            onChange={(ids) => updateField('tradeCategoryIds', ids)}
            required
            error={fieldErrors.tradeCategoryIds}
          />
        ) : null}

        {formData.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES ? (
          <>
            <MultiSelectChecklist
              label="Parent Trade Category Selection"
              options={tradeCategoryOptions}
              selectedIds={formData.parentTradeCategoryIds}
              onChange={(ids) => updateField('parentTradeCategoryIds', ids)}
              required
              error={fieldErrors.parentTradeCategoryIds}
            />
            {(formData.parentTradeCategoryIds || []).length > 0 ? (
              <MultiSelectChecklist
                label="Scope Selection"
                options={subcategoryOptions}
                selectedIds={formData.subcategoryIds}
                onChange={(ids) => updateField('subcategoryIds', ids)}
                required
                error={fieldErrors.subcategoryIds}
              />
            ) : null}
          </>
        ) : null}

        <StackedSelect
          label="Keyword Use"
          value={formData.keywordUse}
          options={KEYWORD_USE_OPTIONS}
          onChange={(v) => updateField('keywordUse', v)}
          required
          error={fieldErrors.keywordUse}
          placeholder="Select Keyword Use"
        />

        {!isAdd ? (
          <ReadOnlyField label="Configuration Status" value={statusDisplay} />
        ) : null}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default KeywordRuleDetail;
